import { useState } from 'react';
import { Alert, Button, Input } from '@dreamtree-org/twreact-ui';
import {
  mpinLogin, startPhoneChallenge, verifyPhoneChallenge, mpinEnroll, mpinReset, enrollDevice, errorOf,
} from '@/lib/tenantAuth';
import { sendPhoneCode } from '@/lib/firebasePhone';

/**
 * Phone + MPIN sign-in (DREAMTREE-MULTITENANT-KNOWLEDGE §5.5). On a recognised device the
 * MPIN alone signs in — no code. A phone proof (Firebase SMS, or an emailed code when the
 * company has no Firebase project) is asked for only to set up an MPIN, reset it, or trust
 * a new device; the server then hands back a single-use capability for that one step.
 *
 *   login  → (deviceVerificationRequired) → verify(device_enroll) → existing MPIN → in
 *   "Set up MPIN" → verify(mpin_enroll) → new MPIN ×2 → in
 *   "Forgot MPIN" → verify(mpin_reset)  → new MPIN ×2 → in (other devices signed out)
 */

const RECAPTCHA_ID = 'phone-recaptcha';

const TITLES = {
  device_enroll: 'Verify this device',
  mpin_enroll: 'Set up your MPIN',
  mpin_reset: 'Reset your MPIN',
};

function deviceLabel() {
  const ua = navigator.userAgent || '';
  return ua.includes('Mobile') ? 'Mobile browser' : 'Browser';
}

export default function PhoneSignIn({ preauthId, firebase, mpinLength = 6, onSignedIn, onExpired }) {
  const [phone, setPhone] = useState('');
  const [mpin, setMpin] = useState('');
  const [mpinConfirm, setMpinConfirm] = useState('');
  const [step, setStep] = useState('login'); // login | verify | finish
  const [purpose, setPurpose] = useState(null);
  const [challenge, setChallenge] = useState(null);
  const [confirmFirebase, setConfirmFirebase] = useState(null);
  const [code, setCode] = useState('');
  const [capability, setCapability] = useState(null);
  const [notice, setNotice] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const run = async (fn) => {
    setErr('');
    setBusy(true);
    try {
      await fn();
    } catch (ex) {
      const { message, code: errCode } = errorOf(ex);
      if (errCode === 'PREAUTH_EXPIRED' || errCode === 'PREAUTH_REQUIRED') onExpired();
      setErr(message);
    } finally {
      setBusy(false);
    }
  };

  const beginVerification = (nextPurpose) => run(async () => {
    setPurpose(nextPurpose);
    setCode('');
    setNotice('');
    const ch = await startPhoneChallenge(preauthId, phone, nextPurpose);
    setChallenge(ch);
    if (ch.provider === 'firebase') {
      setConfirmFirebase(() => null);
      const confirm = await sendPhoneCode(firebase || ch.firebase, phone, RECAPTCHA_ID);
      setConfirmFirebase(() => confirm);
      setNotice('We sent a code by SMS.');
    } else if (ch.instructions.devCode) {
      setNotice(`Development code: ${ch.instructions.devCode}`);
    } else {
      setNotice('If this number belongs to an account, we emailed a code to the account\'s address.');
    }
    setStep('verify');
  });

  const submitLogin = (e) => {
    e.preventDefault();
    run(async () => {
      const res = await mpinLogin(preauthId, phone, mpin);
      if (res.deviceVerificationRequired) {
        setNotice('');
        await beginVerification('device_enroll');
        return;
      }
      setMpin('');
      await onSignedIn(res);
    });
  };

  const submitCode = (e) => {
    e.preventDefault();
    run(async () => {
      const proof = challenge.provider === 'firebase' ? { idToken: await confirmFirebase(code) } : { code };
      const res = await verifyPhoneChallenge(preauthId, challenge.challengeId, phone, purpose, proof);
      setCapability(res.capability);
      setNotice('');
      setStep('finish');
    });
  };

  const submitFinish = (e) => {
    e.preventDefault();
    run(async () => {
      let res;
      if (purpose === 'device_enroll') res = await enrollDevice(preauthId, capability, phone, mpin, deviceLabel());
      else if (purpose === 'mpin_enroll') res = await mpinEnroll(preauthId, capability, mpin, mpinConfirm, deviceLabel());
      else res = await mpinReset(preauthId, capability, mpin, mpinConfirm, deviceLabel());
      setMpin('');
      setMpinConfirm('');
      await onSignedIn(res);
    });
  };

  const backToLogin = () => {
    setStep('login');
    setPurpose(null);
    setChallenge(null);
    setCapability(null);
    setNotice('');
    setErr('');
  };

  const mpinInput = (label, value, set, autoFocus = false) => (
    <Input
      label={label} type="password" inputMode="numeric" autoComplete="off" maxLength={mpinLength}
      value={value} onChange={(e) => set(e.target.value.replace(/\D/g, ''))} autoFocus={autoFocus} required
    />
  );

  return (
    <div className="space-y-3">
      {err && <Alert variant="error">{err}</Alert>}
      {notice && <Alert variant="info">{notice}</Alert>}
      <div id={RECAPTCHA_ID} />

      {step === 'login' && (
        <form className="space-y-3" onSubmit={submitLogin}>
          <Input label="Phone number" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} autoComplete="tel" required />
          {mpinInput('MPIN', mpin, setMpin)}
          <Button type="submit" className="w-full" disabled={busy || !phone.trim() || mpin.length !== mpinLength}>{busy ? 'Signing in…' : 'Sign in'}</Button>
          <div className="flex justify-between text-xs">
            <button type="button" className="text-secondary-500 hover:underline disabled:opacity-50" disabled={!phone.trim() || busy} onClick={() => beginVerification('mpin_enroll')}>Set up MPIN</button>
            <button type="button" className="text-secondary-500 hover:underline disabled:opacity-50" disabled={!phone.trim() || busy} onClick={() => beginVerification('mpin_reset')}>Forgot MPIN?</button>
          </div>
        </form>
      )}

      {step === 'verify' && (
        <form className="space-y-3" onSubmit={submitCode}>
          <p className="text-sm font-medium">{TITLES[purpose]}</p>
          <p className="text-xs text-secondary-500">Enter the 6-digit code for {phone}.</p>
          <Input label="Code" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} inputMode="numeric" autoComplete="one-time-code" maxLength={6} autoFocus required />
          <Button type="submit" className="w-full" disabled={busy || code.length !== 6 || (challenge && challenge.provider === 'firebase' && !confirmFirebase)}>{busy ? 'Checking…' : 'Verify'}</Button>
          <button type="button" className="w-full text-xs text-secondary-500 hover:underline" onClick={backToLogin}>Back</button>
        </form>
      )}

      {step === 'finish' && (
        <form className="space-y-3" onSubmit={submitFinish}>
          <p className="text-sm font-medium">{TITLES[purpose]}</p>
          {purpose === 'device_enroll' ? (
            <>
              <p className="text-xs text-secondary-500">Enter your MPIN to trust this device. You won&apos;t need a code here again.</p>
              {mpinInput('MPIN', mpin, setMpin, true)}
            </>
          ) : (
            <>
              <p className="text-xs text-secondary-500">
                Choose a {mpinLength}-digit MPIN — no repeated or sequential digits.
                {purpose === 'mpin_reset' && ' Your other devices will be signed out.'}
              </p>
              {mpinInput('New MPIN', mpin, setMpin, true)}
              {mpinInput('Confirm MPIN', mpinConfirm, setMpinConfirm)}
            </>
          )}
          <Button type="submit" className="w-full" disabled={busy || mpin.length !== mpinLength || (purpose !== 'device_enroll' && mpinConfirm.length !== mpinLength)}>
            {busy ? 'Saving…' : 'Continue'}
          </Button>
        </form>
      )}
    </div>
  );
}
